import React from 'react';
import { motion } from 'motion/react';
import { Shield, ArrowLeft, Mail, Phone, MapPin, Lock, Eye, Trash2, Cookie, Users, Bell } from 'lucide-react';

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 220, damping: 22 } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <motion.section variants={sectionVariants} className="mb-8">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)' }}>
          <Icon size={15} className="text-emerald-400" />
        </div>
        <h2 className="text-base font-black text-white tracking-tight">{title}</h2>
      </div>
      <div className="text-[13px] text-white/55 leading-relaxed pl-[42px]">
        {children}
      </div>
    </motion.section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div
      className="min-h-screen w-full font-sans flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 60% 0%, #0a1f1a 0%, #040d09 50%, #020807 100%)' }}
    >
      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute top-1/3 -left-48 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 60%)', filter: 'blur(80px)' }} />
      </div>

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center shrink-0" style={{ background: 'rgba(4, 13, 9, 0.85)' }}>
        <motion.button
          whileTap={{ scale: 0.94 }}
          onClick={() => window.history.back()}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs font-bold"
        >
          <ArrowLeft size={15} />
          Back to Home
        </motion.button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-3xl mx-auto px-5 sm:px-6 pt-8 pb-8"
        >
          {/* Page Header */}
          <motion.div variants={sectionVariants} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl blur-md" style={{ background: 'rgba(16,185,129,0.35)' }} />
                <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#064e3b', border: '1px solid #05966980' }}>
                  <Shield size={22} className="text-emerald-400" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Privacy Policy</h1>
                <p className="text-xs text-white/40 mt-0.5">Kangavena Cloud & Luna AI · Sri Lanka</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/30 font-medium">
              <span>Last updated: July 2026</span>
              <span className="text-white/10">·</span>
              <span>Kangavena Cloud, Sri Lanka</span>
            </div>
          </motion.div>

          <Section icon={Eye} title="Information We Collect">
            <p className="mb-3">
              This Privacy Policy governs the use of <strong>Kangavena Cloud</strong> and its proprietary conversational intelligence platform, <strong>Luna AI</strong> ("Luna AI", "we", "us", "our"). We collect information necessary to provide and improve our enterprise POS and conversational messaging services:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">WhatsApp Business Messaging & Communication Data:</strong> When business clients connect their WhatsApp Business accounts to Luna AI, we process customer phone numbers, incoming customer queries, and chat messages strictly to deliver automated customer support, appointment scheduling, and order notifications as directed by the business.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Business Information:</strong> Store name, business type (retail, hotel, garage, healthcare), business address, and operational details needed to configure your POS and AI assistant system.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">User Data:</strong> Your name, email address, and phone number used for account creation and authentication.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Transaction Data:</strong> Sales records, product information, customer purchase history, and payment details processed through the platform.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Location Data:</strong> Store location coordinates used for map features and multi-store management.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Device Information:</strong> Browser type, operating system, and device identifiers for security and compatibility.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Usage Analytics:</strong> Interaction data, feature usage patterns, and performance metrics to improve our services.</span>
              </li>
            </ul>
          </Section>

          <Section icon={Lock} title="How We Use Your Data">
            <p className="mb-3">Your data is used solely for the following purposes:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Provide POS Services:</strong> Operating and maintaining your cloud-based point-of-sale system, including inventory management, billing, and reporting.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Process Transactions:</strong> Facilitating sales, payments, and financial records management within the platform.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Analytics & Improvement:</strong> Generating business insights, dashboards, and using aggregated data to improve platform features and performance.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Security & Fraud Prevention:</strong> Monitoring for suspicious activity, protecting against unauthorized access, and maintaining platform integrity.</span>
              </li>
            </ul>
          </Section>

          <Section icon={Users} title="Data Sharing">
            <p className="mb-3">
              <strong className="text-white/70">We do not sell your personal data.</strong> We will never share your business or personal information with third parties for their marketing purposes.
            </p>
            <p className="mb-3">Data may be shared only in the following limited circumstances:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Payment Processors:</strong> Transaction data may be shared with payment gateway providers to process payments securely.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Cloud Hosting Providers:</strong> Data is stored on secure cloud infrastructure necessary to operate the platform.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Legal Compliance:</strong> We may disclose data if required by law, court order, or governmental authority in Sri Lanka.</span>
              </li>
            </ul>
          </Section>

          <Section icon={Shield} title="Data Security">
            <p className="mb-3">We implement robust security measures to protect your data:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Encrypted Storage:</strong> All sensitive data is encrypted at rest using industry-standard encryption algorithms.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Secure Transmission (HTTPS):</strong> All data transmitted between your devices and our servers is encrypted via TLS/SSL.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Access Controls:</strong> Role-based access ensures that each user can only access the data relevant to their role. Cross-tenant data access is structurally impossible.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Regular Backups:</strong> Automated backup systems ensure data durability and quick recovery in case of any issues.</span>
              </li>
            </ul>
          </Section>

          <Section icon={Eye} title="Your Rights">
            <p className="mb-3">You have full control over your data:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Access:</strong> You can view all your data stored within the POS system through your dashboard.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Correction:</strong> You can update or correct your personal and business information at any time through the app settings.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Deletion:</strong> You can request deletion of your account and associated data directly within the app, or by contacting us. Account deletion is permanent and removes all your data from our active systems.</span>
              </li>
            </ul>
          </Section>

          <Section icon={Cookie} title="Cookies">
            <p className="mb-3">
              We use <strong className="text-white/70">session cookies</strong> for authentication purposes. These cookies are essential for maintaining your logged-in session and ensuring secure access to the platform. They are automatically cleared when you close your browser or log out.
            </p>
            <p>
              We do not use third-party tracking cookies or advertising cookies. Session cookies contain no personally identifiable information and are strictly used for functional authentication.
            </p>
          </Section>

          <Section icon={Users} title="Children's Privacy">
            <p>
              Kangavena Cloud is a business-to-business platform and is not directed at children under the age of 13. We do not knowingly collect personal information from children. If we discover that we have inadvertently collected data from a child under 13, we will promptly delete it. If you believe a child has provided us with personal information, please contact us immediately.
            </p>
          </Section>

          <Section icon={Bell} title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make material changes, we will notify users through the platform or via email. The "Last updated" date at the top of this page indicates when the policy was most recently revised.
            </p>
          </Section>

          <Section icon={Mail} title="Contact Us">
            <p className="mb-3">
              If you have any questions about this Privacy Policy or how we handle your data, please reach out:
            </p>
            <div className="rounded-2xl p-4 mt-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
              <div className="space-y-2.5">
                <a href="mailto:sissara.com@gmail.com" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Mail size={14} className="text-emerald-500" />
                  <span className="text-sm">sissara.com@gmail.com</span>
                </a>
                <a href="https://wa.me/94767114888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Phone size={14} className="text-emerald-500" />
                  <span className="text-sm">076 711 4888 (WhatsApp)</span>
                </a>
                <div className="flex items-center gap-2.5 text-white/60">
                  <MapPin size={14} className="text-emerald-500" />
                  <span className="text-sm">Sri Lanka</span>
                </div>
              </div>
            </div>
          </Section>

          {/* Back to Home */}
          <motion.div variants={sectionVariants} className="text-center pt-4 pb-8">
            <a href="/" className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400/60 hover:text-emerald-400 transition-colors">
              <ArrowLeft size={13} />
              Back to Home
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
